import React, { useEffect, useRef, useState } from 'react';
import Container from './styles';

const AddressAutocompleteInput = ({ 
  value, 
  onChange, 
  onPlaceSelected, 
  placeholder = "Enter address",
  name,
  required = false,
  disabled = false,
  autoFocus = false,
  stateFilter = ''
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-focus when autoFocus prop changes to true
  useEffect(() => {
    if (autoFocus && inputRef.current && !disabled) {
      inputRef.current.focus();
    }
  }, [autoFocus, disabled]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      // Build search query with state and Nigeria filter
      const searchQuery = stateFilter 
        ? `${query}, ${stateFilter}, Nigeria`
        : `${query}, Nigeria`;

      // Using Nominatim OpenStreetMap API (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `format=json&` +
        `q=${encodeURIComponent(searchQuery)}&` +
        `countrycodes=ng&` + // Restrict to Nigeria only
        `limit=5&` +
        `addressdetails=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'PickupLogistics/1.0' // Required by Nominatim usage policy
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      } else {
        console.error('Address search failed:', response.statusText);
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching address:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    
    // Call parent onChange
    if (onChange) {
      onChange(e);
    }

    // Debounce the API call
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300); // Wait 300ms after user stops typing
  };

  const handleSelectSuggestion = (suggestion) => {
    const addressData = {
      address: suggestion.display_name,
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
      placeId: suggestion.place_id,
    };

    // Call the parent callback with selected place data
    if (onPlaceSelected) {
      onPlaceSelected(addressData);
    }

    // Also trigger onChange with the address string
    if (onChange) {
      onChange({
        target: {
          name: name,
          value: addressData.address
        }
      });
    }

    setSuggestions([]);
    setShowDropdown(false);
  };

  return (
    <Container ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        name={name}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />
      
      {loading && (
        <div className="loading-indicator">
          <small>Searching addresses...</small>
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div className="suggestions-dropdown">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="suggestion-item"
              onClick={() => handleSelectSuggestion(suggestion)}
            >
              <div className="suggestion-icon">📍</div>
              <div className="suggestion-content">
                <div className="suggestion-main" style={{ color: '#000000' }}>
                  {suggestion.display_name.split(',')[0]}
                </div>
                <div className="suggestion-secondary" style={{ color: '#666666' }}>
                  {suggestion.display_name}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
};

export default AddressAutocompleteInput;
